import React, { useState, useEffect } from 'react';
import {
  Card, Upload, Button, Select, Table, Space, Tag, message, Spin, Typography, Row, Col, Descriptions, Image
} from 'antd';
import { UploadOutlined, ThunderboltOutlined, DeleteOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const FIELD_LABELS = {
  fullName: '姓名（英文）',
  chineseName: '中文姓名',
  passportNumber: '护照号码',
  gender: '性别',
  nationality: '国籍',
  birthDate: '出生日期',
  issueDate: '签发日期',
  expiryDate: '有效期至',
  birthPlace: '出生地',
};

export default function OCRTest() {
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState(['gpt-4o-mini']);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE}/ocr-test/models`).then(res => {
      if (res.data.success) setModels(res.data.data);
    });
  }, []);

  const handleFileSelect = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleRecognize = async () => {
    if (!imageFile) {
      message.warning('请先上传护照图片');
      return;
    }
    if (selectedModels.length === 0) {
      message.warning('请选择至少一个模型');
      return;
    }

    // 并行调用所有选中的模型
    const newLoading = {};
    selectedModels.forEach(m => { newLoading[m] = true; });
    setLoading(newLoading);

    const promises = selectedModels.map(async (modelKey) => {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('model', modelKey);

      try {
        const res = await axios.post(`${API_BASE}/ocr-test/recognize`, formData);
        return { modelKey, ...res.data, timestamp: new Date().toLocaleTimeString() };
      } catch (err) {
        return {
          modelKey,
          success: false,
          error: err.response?.data?.error || err.message,
          model: models.find(m => m.key === modelKey)?.name || modelKey,
          duration: 0,
          timestamp: new Date().toLocaleTimeString(),
        };
      } finally {
        setLoading(prev => ({ ...prev, [modelKey]: false }));
      }
    });

    const newResults = await Promise.all(promises);
    setResults(prev => [...newResults, ...prev]);
  };

  const clearResults = () => setResults([]);

  // 对比表格：所有字段横向对比
  const comparisonData = Object.keys(FIELD_LABELS).map(field => {
    const row = { field, label: FIELD_LABELS[field] };
    results.forEach((r, i) => {
      row[`result_${i}`] = r.success ? (r.data?.[field] || '-') : '-';
    });
    return row;
  });

  const comparisonColumns = [
    {
      title: '字段',
      dataIndex: 'label',
      key: 'label',
      width: 120,
      fixed: 'left',
      render: (text) => <Text strong>{text}</Text>,
    },
    ...results.map((r, i) => ({
      title: (
        <div>
          <Tag color={r.success ? 'green' : 'red'}>{r.model}</Tag>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.duration}ms | {r.timestamp}
          </Text>
        </div>
      ),
      dataIndex: `result_${i}`,
      key: `result_${i}`,
      width: 180,
      render: (val) => val === '-' ? <Text type="secondary">-</Text> : val,
    })),
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <Title level={3}>
        <ThunderboltOutlined style={{ marginRight: 8 }} />
        护照 OCR 模型测试
      </Title>

      <Row gutter={24}>
        {/* 左侧：上传 + 控制 */}
        <Col span={8}>
          <Card title="上传护照图片" size="small">
            <Upload.Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleFileSelect}
              style={{ marginBottom: 16 }}
            >
              {imagePreview ? (
                <Image src={imagePreview} preview={false} style={{ maxHeight: 300, objectFit: 'contain' }} />
              ) : (
                <div style={{ padding: '20px 0' }}>
                  <UploadOutlined style={{ fontSize: 32, color: '#999' }} />
                  <p>点击或拖拽上传护照图片</p>
                </div>
              )}
            </Upload.Dragger>

            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>选择模型：</Text>
              <Select
                mode="multiple"
                value={selectedModels}
                onChange={setSelectedModels}
                style={{ width: '100%' }}
                placeholder="选择要测试的模型"
              >
                {models.map(m => (
                  <Select.Option key={m.key} value={m.key} disabled={!m.available}>
                    {m.name} {!m.available && '(未配置)'}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <Space style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleRecognize}
                loading={Object.values(loading).some(Boolean)}
                disabled={!imageFile || selectedModels.length === 0}
                block
              >
                开始识别
              </Button>
              <Button icon={<ClearOutlined />} onClick={clearResults} disabled={results.length === 0}>
                清空
              </Button>
            </Space>
          </Card>

          {/* 单次结果详情 */}
          {results.length > 0 && (
            <Card title="最新结果详情" size="small" style={{ marginTop: 16 }}>
              {results.slice(0, 1).map((r, i) => (
                <div key={i}>
                  <Space style={{ marginBottom: 8 }}>
                    <Tag color={r.success ? 'green' : 'red'}>{r.success ? '成功' : '失败'}</Tag>
                    <Tag>{r.model}</Tag>
                    <Tag color="blue">{r.duration}ms</Tag>
                    {r.tokens && (
                      <Tag color="purple">
                        {r.tokens.prompt_tokens}+{r.tokens.completion_tokens} tokens
                      </Tag>
                    )}
                  </Space>
                  {r.success && r.data && (
                    <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                      {Object.entries(FIELD_LABELS).map(([key, label]) => (
                        <Descriptions.Item key={key} label={label}>
                          {r.data[key] || <Text type="secondary">-</Text>}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  )}
                  {!r.success && (
                    <Text type="danger">{r.error}</Text>
                  )}
                </div>
              ))}
            </Card>
          )}
        </Col>

        {/* 右侧：对比表格 */}
        <Col span={16}>
          <Card
            title={`结果对比 (${results.length} 次识别)`}
            size="small"
            extra={
              <Space>
                {Object.entries(loading).map(([model, isLoading]) =>
                  isLoading ? <Spin key={model} size="small" /> : null
                )}
              </Space>
            }
          >
            {results.length > 0 ? (
              <Table
                columns={comparisonColumns}
                dataSource={comparisonData}
                rowKey="field"
                size="small"
                pagination={false}
                scroll={{ x: 120 + results.length * 180 }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                上传护照图片并选择模型后点击"开始识别"
              </div>
            )}
          </Card>

          {/* 原始返回 */}
          {results.filter(r => r.raw).length > 0 && (
            <Card title="模型原始返回" size="small" style={{ marginTop: 16 }}>
              {results.filter(r => r.raw).map((r, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <Tag color="blue">{r.model}</Tag>
                  <Tag>{r.timestamp}</Tag>
                  <pre style={{
                    background: '#f5f5f5', padding: 12, borderRadius: 4,
                    fontSize: 12, maxHeight: 200, overflow: 'auto',
                    whiteSpace: 'pre-wrap', marginTop: 8
                  }}>
                    {r.raw}
                  </pre>
                </div>
              ))}
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
